import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../config/firebase';
import { collection, getDocs, doc, deleteDoc, addDoc, updateDoc, Timestamp, query, where } from 'firebase/firestore';
import './AdminPanel.css';

const unitLogo = '/images/LP_Unit_logo.png';

function AdminPanel() {
  const { user, userData, isAdmin, logout, getAllUsers, updateUserRole, approveUser, rejectUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('users');
  
  // Reject modal state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectUserId, setRejectUserId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  
  // Sorting state for User Management
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Check URL for tab parameter
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab === 'donations') {
      setActiveTab('donations');
    }
  }, []);

  // Fetch users, requests, and donations
  useEffect(() => {
    const fetchData = async () => {
      try {
        const usersList = await getAllUsers();
        setUsers(usersList || []);
        
        // Fetch requests
        const requestsSnap = await getDocs(collection(db, 'requests'));
        const requestsList = requestsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setRequests(requestsList);
        
        // Fetch donations
        const donationsSnap = await getDocs(collection(db, 'donations'));
        const donationsList = donationsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setDonations(donationsList);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin, getAllUsers]);

  // Redirect if not admin (after hooks)
  if (!isAdmin) {
    return <Navigate to="/no-clearance" />;
  }

  const handleApprove = async (userId) => {
    const result = await approveUser(userId);
    if (result.success) {
      setUsers(users.map(u =>
        u.id === userId ? { ...u, approved: true, approvedAt: new Date() } : u
      ));
    }
  };

  const openRejectModal = (userId) => {
    setRejectUserId(userId);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleRejectUser = async () => {
    if (!rejectUserId) return;

    const userToReject = users.find(u => u.id === rejectUserId);

    try {
      // Log the rejection to auditLogs
      await addDoc(collection(db, 'auditLogs'), {
        action: 'REJECT_USER',
        rejectedBy: user.uid,
        rejectedByEmail: userData?.email || '',
        rejectedByName: userData?.username || userData?.email || '',
        rejectedAt: Timestamp.now(),
        reason: rejectReason || 'No reason provided',
        rejectedUserId: rejectUserId,
        rejectedUserEmail: userToReject?.email || 'Unknown',
        rejectedUsername: userToReject?.username || 'Unknown'
      });

      // Delete the user document from Firestore
      await deleteDoc(doc(db, 'users', rejectUserId));

      // Update local state
      setUsers(users.filter(u => u.id !== rejectUserId));

      // Close modal
      setShowRejectModal(false);
      setRejectUserId(null);
      setRejectReason('');
    } catch (error) {
      console.error('Error rejecting user:', error);
      alert('Error rejecting user');
    }
  };

  // Sort users based on sortBy and sortOrder
  const getSortedUsers = (usersList) => {
    return [...usersList].sort((a, b) => {
      let comparison = 0;

      if (sortBy === 'date') {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        comparison = dateA - dateB;
      } else if (sortBy === 'username') {
        const nameA = (a.username || '').toLowerCase();
        const nameB = (b.username || '').toLowerCase();
        comparison = nameA.localeCompare(nameB);
      } else if (sortBy === 'role') {
        const roleOrder = { admin: 1, armorer: 2, user: 3 };
        comparison = (roleOrder[a.role] || 4) - (roleOrder[b.role] || 4);
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });
  };


  const handleRoleChange = async (userId, newRole) => {
    const result = await updateUserRole(userId, newRole);
    if (result.success) {
      setUsers(users.map(u => 
        u.id === userId ? { ...u, role: newRole } : u
      ));
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (window.confirm('Are you sure you want to delete this request? This cannot be undone.')) {
      try {
        // Find the request to log its details before deleting
        const requestToDelete = requests.find(r => r.id === requestId);
        
        // Log the delete action to auditLogs collection
        await addDoc(collection(db, 'auditLogs'), {
          action: 'DELETE_REQUEST',
          deletedBy: user.uid,
          deletedByEmail: userData?.email || '',
          deletedByName: userData?.username || userData?.email || '',
          deletedAt: Timestamp.now(),
          requestId: requestId,
          requestType: requestToDelete?.type || 'Unknown',
          requestUser: requestToDelete?.username || requestToDelete?.userEmail || 'Unknown',
          requestZap: requestToDelete?.zap || 'N/A',
          requestDetails: requestToDelete || {}
        });
        
        // Now delete the request
        await deleteDoc(doc(db, 'requests', requestId));
        setRequests(requests.filter(r => r.id !== requestId));
      } catch (error) {
        console.error('Error deleting request:', error);
        alert('Error deleting request');
      }
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatPerkTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Determine which perks are available based on tier
  const getTierLevel = (tierName) => {
    switch(tierName) {
      case 'JSOC': return 4; // Gets all perks
      case 'Tier 1': return 3; // Gets Tier 1, 2, 3
      case 'Tier 2': return 2; // Gets Tier 2, 3
      case 'Tier 3': return 1; // Gets Tier 3 only
      default: return 0;
    }
  };

  const isPerkAvailable = (donationTier, perkTier) => {
    const tierLevel = getTierLevel(donationTier);
    switch(perkTier) {
      case 'jsoc': return tierLevel >= 4;
      case 'tier1': return tierLevel >= 3;
      case 'tier2': return tierLevel >= 2;
      case 'tier3': return tierLevel >= 1;
      default: return false;
    }
  };

  const handlePerkChange = async (donationId, perkField, newValue) => {
    try {
      const donationRef = doc(db, 'donations', donationId);
      const updateData = {
        [perkField]: newValue
      };
      
      // Add timestamp if marking as "Yes"
      if (newValue === 'Yes') {
        updateData[`${perkField}Date`] = Timestamp.now();
      } else {
        updateData[`${perkField}Date`] = null;
      }
      
      await updateDoc(donationRef, updateData);
      
      // Update local state
      setDonations(donations.map(d => 
        d.id === donationId 
          ? { ...d, [perkField]: newValue, [`${perkField}Date`]: newValue === 'Yes' ? new Date() : null }
          : d
      ));
    } catch (error) {
      console.error('Error updating perk status:', error);
      alert('Error updating perk status');
    }
  };

  const handleDonationStatusChange = async (donationId, newStatus) => {
    try {
      const donationRef = doc(db, 'donations', donationId);
      await updateDoc(donationRef, { status: newStatus });
      
      // Update local state
      setDonations(donations.map(d => 
        d.id === donationId ? { ...d, status: newStatus } : d
      ));
    } catch (error) {
      console.error('Error updating donation status:', error);
      alert('Error updating donation status');
    }
  };

  const getDonationStatusColor = (status) => {
    switch(status) {
      case 'Pending': return '#ffd93d';
      case 'Partially Complete': return '#4d96ff';
      case 'Completed': return '#6bcb77';
      case 'Denied': return '#ff6b6b';
      default: return '#888';
    }
  };

  // User counts
  const userCount = users.filter(u => u.role === 'user').length;
  const armorerCount = users.filter(u => u.role === 'armorer').length;
  const adminCount = users.filter(u => u.role === 'admin').length;
  const pendingCount = users.filter(u => !u.approved).length;

  const filteredUsers = getSortedUsers(
    filter === 'all' 
      ? users 
      : filter === 'pending' 
        ? users.filter(u => !u.approved)
        : users.filter(u => u.role === filter)
  );

  return (
    <div className="admin-page" style={{ backgroundImage: `url(/images/CSQD-Vol-II.jpg)`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
      {/* Navigation */}
      <nav className="admin-navbar">
        <Link to="/" className="nav-left">
          <img src={unitLogo} alt="Logo" className="unit-logo" />
          <h1 className="site-title">Admin Panel</h1>
        </Link>
        <div className="nav-right">
          <Link to="/" className="nav-link">Home</Link>
          <Link to="/requests" className="nav-link">Requests</Link>
          <span className="user-badge admin-badge">ADMIN</span>
          <button className="logout-btn" onClick={logout}>Logout</button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="admin-content">
        {/* Tab Navigation */}
        <div className="tab-nav">
          <button 
            className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            User Management
          </button>
          <button 
            className={`tab-btn ${activeTab === 'requests' ? 'active' : ''}`}
            onClick={() => setActiveTab('requests')}
          >
            All Requests
          </button>
          <button 
            className={`tab-btn ${activeTab === 'donations' ? 'active' : ''}`}
            onClick={() => setActiveTab('donations')}
          >
            Donation Requests
          </button>
        </div>

        {activeTab === 'users' && (
          <>
            <div className="admin-header">
              <h2>User Management</h2>
              <div className="stats-row">
                <div className="stat-item">
                  <span className="stat-num">{users.length}</span>
                  <span className="stat-label">Total Users</span>
                </div>
                <div className="stat-item">
                  <span className="stat-num">{userCount}</span>
                  <span className="stat-label">Users</span>
                </div>
                <div className="stat-item">
                  <span className="stat-num">{armorerCount}</span>
                  <span className="stat-label">Armorers</span>
                </div>
                <div className="stat-item">
                  <span className="stat-num">{adminCount}</span>
                  <span className="stat-label">Admins</span>
                </div>
                <div className="stat-item pending">
                  <span className="stat-num">{pendingCount}</span>
                  <span className="stat-label">Pending</span>
                </div>
              </div>
            </div>

            {/* Filter and Sort */}
            <div className="filter-bar">
              <div className="filter-group">
                <label>Filter:</label>
                <select value={filter} onChange={(e) => setFilter(e.target.value)}>
                  <option value="all">All Users</option>
                  <option value="pending">Pending Approval</option>
                  <option value="user">Users</option>
                  <option value="armorer">Armorers</option>
                  <option value="admin">Admins</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Sort by:</label>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="date">Date Created</option>
                  <option value="username">Username</option>
                  <option value="role">Role</option>
                </select>
                <button 
                  className="sort-order-btn"
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>
            </div>

            {/* Users Table */}
            <div className="users-table-container">
              {loading ? (
                <div className="loading">Loading users...</div>
              ) : filteredUsers.length === 0 ? (
                <div className="no-users">No users found</div>
              ) : (
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(u => (
                      <tr key={u.id}>
                        <td className="username-cell">{u.username || 'N/A'}</td>
                        <td>{u.email}</td>
                        <td>
                          <select
                            value={u.role}
                            onChange={(e) => handleRoleChange(u.id, e.target.value)}
                            className="role-select"
                          >
                            <option value="user">User</option>
                            <option value="armorer">Armorer</option>
                            <option value="admin">Admin</option>
                          </select>
                        </td>
                        <td>
                          {u.approved ? (
                            <span className="status-badge approved">Approved</span>
                          ) : (
                            <span className="status-badge pending">Pending</span>
                          )}
                        </td>
                        <td>{formatDate(u.createdAt)}</td>
                        <td className="actions-cell">
                          {!u.approved && (
                            <>
                              <button
                                className="approve-btn"
                                onClick={() => handleApprove(u.id)}
                              >
                                Approve
                              </button>
                              <button
                                className="reject-btn"
                                onClick={() => openRejectModal(u.id)}
                              >
                                Reject
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {activeTab === 'requests' && (
          <>
            <div className="admin-header">
              <h2>All Commission Requests</h2>
              <div className="stats-row">
                <div className="stat-item">
                  <span className="stat-num">{requests.length}</span>
                  <span className="stat-label">Total</span>
                </div>
                <div className="stat-item">
                  <span className="stat-num">{requests.filter(r => r.type === 'Vest').length}</span>
                  <span className="stat-label">Vests</span>
                </div>
                <div className="stat-item">
                  <span className="stat-num">{requests.filter(r => r.type === 'Helmet').length}</span>
                  <span className="stat-label">Helmets</span>
                </div>
              </div>
            </div>

            <div className="users-table-container">
              {requests.length === 0 ? (
                <div className="no-users">No requests found</div>
              ) : (
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>User</th>
                      <th>ZAP</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...requests].sort((a, b) => {
                      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
                      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
                      return dateB - dateA; // Newest first
                    }).map(r => (
                      <tr key={r.id}>
                        <td>{formatDate(r.createdAt)}</td>
                        <td>{r.username || r.userEmail}</td>
                        <td>{r.zap}</td>
                        <td><span className={`type-badge ${r.type?.toLowerCase().replace(/\s+/g, '-')}`}>{r.type}</span></td>
                        <td>{r.status}</td>
                        <td>
                          <button 
                            className="delete-btn"
                            onClick={() => handleDeleteRequest(r.id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {activeTab === 'donations' && (
          <>
            <div className="admin-header">
              <h2>Donation Requests</h2>
              <p className="admin-subtitle">Manage donation submissions and perk redemptions</p>
            </div>

            <div className="admin-content donations-content">
              {donations.length === 0 ? (
                <div className="no-data">No donation requests yet</div>
              ) : (
                <div className="donations-table-container">
                  <table className="admin-table donations-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Discord Name</th>
                        <th>Tier</th>
                        <th>Amount Sent</th>
                        <th>PayPal Email</th>
                        <th>Status</th>
                        <th>JSOC Perks</th>
                        <th>Tier 1 Perks</th>
                        <th>Tier 2 Perks</th>
                        <th>Tier 3 Perks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...donations].sort((a, b) => {
                        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
                        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
                        return dateB - dateA; // Newest first
                      }).map(d => (
                        <tr key={d.id}>
                          <td>{formatDate(d.createdAt)}</td>
                          <td>{d.discordName}</td>
                          <td><span className={`tier-badge tier-${d.tier?.toLowerCase().replace(/\s+/g, '-')}`}>{d.tier} - ${d.tierPrice}</span></td>
                          <td>${d.amountSent}</td>
                          <td>{d.paypalEmail}</td>
                          <td>
                            <select
                              value={d.status || 'Pending'}
                              onChange={(e) => handleDonationStatusChange(d.id, e.target.value)}
                              className="donation-status-select"
                              style={{ borderColor: getDonationStatusColor(d.status) }}
                            >
                              <option value="Pending">Pending</option>
                              <option value="Partially Complete">Partially Complete</option>
                              <option value="Completed">Completed</option>
                              <option value="Denied">Denied</option>
                            </select>
                          </td>
                          
                          {/* JSOC Perks */}
                          <td className="perk-cell">
                            {isPerkAvailable(d.tier, 'jsoc') ? (
                              <div className="perk-dropdown-wrapper">
                                <select
                                  value={d.jsocPerks || 'No'}
                                  onChange={(e) => handlePerkChange(d.id, 'jsocPerks', e.target.value)}
                                  className={`perk-select ${d.jsocPerks === 'Yes' ? 'perk-yes' : 'perk-no'}`}
                                >
                                  <option value="No">No</option>
                                  <option value="Yes">Yes</option>
                                </select>
                                {d.jsocPerks === 'Yes' && d.jsocPerksDate && (
                                  <span className="perk-date">{formatPerkTimestamp(d.jsocPerksDate)}</span>
                                )}
                              </div>
                            ) : (
                              <span className="perk-na">N/A</span>
                            )}
                          </td>
                          
                          {/* Tier 1 Perks */}
                          <td className="perk-cell">
                            {isPerkAvailable(d.tier, 'tier1') ? (
                              <div className="perk-dropdown-wrapper">
                                <select
                                  value={d.tier1Perks || 'No'}
                                  onChange={(e) => handlePerkChange(d.id, 'tier1Perks', e.target.value)}
                                  className={`perk-select ${d.tier1Perks === 'Yes' ? 'perk-yes' : 'perk-no'}`}
                                >
                                  <option value="No">No</option>
                                  <option value="Yes">Yes</option>
                                </select>
                                {d.tier1Perks === 'Yes' && d.tier1PerksDate && (
                                  <span className="perk-date">{formatPerkTimestamp(d.tier1PerksDate)}</span>
                                )}
                              </div>
                            ) : (
                              <span className="perk-na">N/A</span>
                            )}
                          </td>
                          
                          {/* Tier 2 Perks */}
                          <td className="perk-cell">
                            {isPerkAvailable(d.tier, 'tier2') ? (
                              <div className="perk-dropdown-wrapper">
                                <select
                                  value={d.tier2Perks || 'No'}
                                  onChange={(e) => handlePerkChange(d.id, 'tier2Perks', e.target.value)}
                                  className={`perk-select ${d.tier2Perks === 'Yes' ? 'perk-yes' : 'perk-no'}`}
                                >
                                  <option value="No">No</option>
                                  <option value="Yes">Yes</option>
                                </select>
                                {d.tier2Perks === 'Yes' && d.tier2PerksDate && (
                                  <span className="perk-date">{formatPerkTimestamp(d.tier2PerksDate)}</span>
                                )}
                              </div>
                            ) : (
                              <span className="perk-na">N/A</span>
                            )}
                          </td>
                          
                          {/* Tier 3 Perks */}
                          <td className="perk-cell">
                            {isPerkAvailable(d.tier, 'tier3') ? (
                              <div className="perk-dropdown-wrapper">
                                <select
                                  value={d.tier3Perks || 'No'}
                                  onChange={(e) => handlePerkChange(d.id, 'tier3Perks', e.target.value)}
                                  className={`perk-select ${d.tier3Perks === 'Yes' ? 'perk-yes' : 'perk-no'}`}
                                >
                                  <option value="No">No</option>
                                  <option value="Yes">Yes</option>
                                </select>
                                {d.tier3Perks === 'Yes' && d.tier3PerksDate && (
                                  <span className="perk-date">{formatPerkTimestamp(d.tier3PerksDate)}</span>
                                )}
                              </div>
                            ) : (
                              <span className="perk-na">N/A</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </main>

      {/* Reject User Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="modal-content reject-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowRejectModal(false)}>×</button>
            <h2 className="modal-title">Reject User</h2>
            <p className="modal-warning">This will permanently delete the user account.</p>
            <div className="modal-field">
              <label>Reason for Rejection (optional):</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                className="reject-reason-textarea"
                rows={4}
              />
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowRejectModal(false)}>Cancel</button>
              <button className="btn-reject" onClick={handleRejectUser}>Reject & Delete User</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;
